/**
 * Builds the To/Cc recipient lists for the meeting follow-up email.
 *
 * The backend does NOT guarantee that `engagement.contacts` are external:
 * anyone on the meeting with a Contact record shows up there, including
 * workspace users (e.g. when the workspace tracks its own company as an
 * account). Treat the engagement's `users` emails as the definition of
 * "internal" and subtract them from To, so teammates only appear in Cc.
 */

import { getDisplayName } from "@/lib/utils/display-name";

interface ContactLike {
  primaryEmail?: string | null;
  properties?: {
    firstName?: string | null;
    lastName?: string | null;
  } | null;
}

interface UserLike {
  email?: string | null;
  preferredName?: string | null;
  givenName?: string | null;
  surname?: string | null;
}

export interface FollowUpRecipient {
  name: string;
  email: string;
}

export interface FollowUpRecipientDetails {
  /** External contacts (never contains an internal user). */
  to: FollowUpRecipient[];
  /** Internal workspace users, excluding the sender. */
  cc: FollowUpRecipient[];
}

/** Dedupes by email case-insensitively, keeping the first occurrence. */
function dedupeRecipients(recipients: FollowUpRecipient[]): FollowUpRecipient[] {
  const seen = new Set<string>();
  const out: FollowUpRecipient[] = [];
  for (const r of recipients) {
    const key = r.email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  return out;
}

function userDisplayName(u: UserLike): string {
  const name = [u.preferredName || u.givenName, u.surname].filter(Boolean).join(" ").trim();
  return name || getDisplayName(null, null, u.email);
}

export function getFollowUpRecipientDetails(
  contacts: ReadonlyArray<ContactLike | null | undefined> | null | undefined,
  users: ReadonlyArray<UserLike | null | undefined> | null | undefined,
  currentUserEmail: string | null | undefined,
): FollowUpRecipientDetails {
  const senderEmail = currentUserEmail?.toLowerCase();

  const internalUsers = (users ?? []).filter((u): u is UserLike => !!u?.email);
  const internalSet = new Set(internalUsers.map((u) => u.email!.toLowerCase()));

  const to = dedupeRecipients(
    (contacts ?? [])
      .filter((c): c is ContactLike => {
        const email = c?.primaryEmail;
        if (!email) return false;
        const key = email.toLowerCase();
        return key !== senderEmail && !internalSet.has(key);
      })
      .map((c) => ({
        name: getDisplayName(c.properties?.firstName, c.properties?.lastName, c.primaryEmail),
        email: c.primaryEmail!,
      })),
  );

  const cc = dedupeRecipients(
    internalUsers
      .filter((u) => u.email!.toLowerCase() !== senderEmail)
      .map((u) => ({ name: userDisplayName(u), email: u.email! })),
  );

  return { to, cc };
}
